import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";

import { section } from "@/glow";
import { on, emit } from "@/glow/events";
import { provideResizeObserver } from "@/utils";
import { makeRequest } from "@/lib/xhr";
import Delegate from "ftdomdelegate";

import QuickSearch from "@/lib/header/quick-search";
import Navigation from "@/lib/header/navigation";
import AccountQuickView from "@/lib/header/account-quick-view";
import PurchaseConfirmationPopup from "@/lib/header/purchase-confirmation-popup";
import { activateTemplate } from "@/lib/templates";
import updateInnerHTML from "@/lib/update-inner-html";

function setHeaderHeightVar(height) {
  document.documentElement.style.setProperty(
    "--height-header",
    `${Math.ceil(height)}px`
  );
}

function setHeaderStickyHeaderHeight(value) {
  document.documentElement.style.setProperty(
    "--header-desktop-sticky-height",
    `${value}px`
  );
}

function setNavBarHeightVar(height) {
  document.documentElement.style.setProperty(
    "--nav-bar-height",
    `${Math.ceil(height)}px`
  );
}

const selectors = {
  cartTrigger: "[data-header-cart]",
  desktopCartTrigger: `.header__content-desktop [data-header-cart]`,
  mobileCartTrigger: `.header__content-mobile [data-header-cart]`,
  menuButtons: "[data-menu-button]",
  quickSearch: "[data-quick-search]",
  headerSpace: "[data-header-space]",
  purchaseConfirmation: "[data-purchase-confirmation-popup]",
  navigation: "[data-navigation]",
  navigationBar: "[data-navigation-bar]",
  navBarButton: "[data-nav-bar-button]",
  accountQuickView: "[data-account-quick-view]",
  mobileSearchIcon: ".header__content-mobile .header__icon-touch--search",
};

const classes = {
  headerTransparent: "header-transparent",
  stickyEnabled: "sticky-header-enabled",
  stickyActive: "sticky-header-active",
  hideNavBar: "nav-bar-hidden",
  navBarToggleVisible: "nav-bar-toggle-visible",
  isSticky: "is-sticky",
  active: "active",
  dropdownActive: "dropdown-active",
};

section("header", {
  crossBorder: {},

  onLoad() {
    const {
      enableStickyHeader,
      transparentHeader,
      navigationInteraction,
      searchEnabled,
    } = this.container.dataset;
    const menuButtons = qsa(selectors.menuButtons, this.container);
    const headerSpace = qs(selectors.headerSpace, document);
    const navBar = qs(selectors.navigationBar, document);
    const navBarToggle = qs(selectors.navBarButton, this.container);
    const quickSearchContainers = qsa(selectors.quickSearch, this.container);
    const quickSearchMobileIcon = qs(
      selectors.mobileSearchIcon,
      this.container
    );
    this.meganavOpenedFromDesignMode = false;

    if (window.Shopify.designMode) {
      // In the theme editor we want the templates activated up front
      qsa("template", this.container).forEach(templateEl => {
        activateTemplate(templateEl.getAttribute("data-template-key"));
      });
    }

    this.purchaseConfirmationPopup = PurchaseConfirmationPopup(
      qs(selectors.purchaseConfirmation, document)
    );
    this.navigation = Navigation(
      qs(selectors.navigation, this.container),
      this.container,
      { interaction: navigationInteraction }
    );
    this.accountQuickView = AccountQuickView(qs(selectors.accountQuickView));

    // This is done here AND in the liquid so it is responsive in TE but doesn't wait for JS otherwise
    document.body.classList.toggle(
      classes.headerTransparent,
      Boolean(transparentHeader)
    );
    document.documentElement.classList.toggle(
      classes.stickyEnabled,
      Boolean(enableStickyHeader)
    );

    // Using a delegate as fetching the header content to refresh prices / cart count
    // will flush attached events.
    const delegate = new Delegate(this.container);

    delegate.on("click", selectors.cartTrigger, e => {
      const quickShop = qs(".quick-cart", document);
      if (!quickShop) return;

      e.preventDefault();
      emit("quick-cart:open");
    });

    let hasHidden;
    document.addEventListener("visibilitychange", () => {
      if (
        !hasHidden &&
        (hasHidden = document.visibilityState === "hidden") &&
        navigator.sendBeacon
      ) {
        // eslint-disable-next-line no-undef , no-process-env
        navigator.sendBeacon(process.env.FLU_PING_ENDPOINT, Shopify.shop);
      }
    });

    // These all return a function for cleanup
    this.listeners = [
      on("cart:updated", () => {
        // Fetching header to ensure correct money formatting
        makeRequest("GET", `/?section_id=${this.id}`).then(response => {
          const container = document.createElement("div");
          container.innerHTML = response;

          updateInnerHTML(selectors.mobileCartTrigger, container);
          updateInnerHTML(selectors.desktopCartTrigger, container);

          this.accountQuickView?.unload();
          this.accountQuickView = AccountQuickView(
            qs(selectors.accountQuickView)
          );
        });
      }),
      listen(menuButtons, "click", function (event) {
        event.preventDefault();
        emit("mobile-menu:open");
      }),
      on("search:focused", () => {
        this.navigation.closeAll();
      }),
    ];

    if (navBar) {
      this.listeners.push(
        listen(navBarToggle, "click", e => {
          const navIsVisble =
            e.currentTarget.getAttribute("aria-expanded") == "true";
          toggle(document.documentElement, classes.hideNavBar, navIsVisble);

          if (navIsVisble) {
            e.currentTarget.setAttribute("aria-expanded", false);
          } else {
            e.currentTarget.setAttribute("aria-expanded", true);
          }
        })
      );
    }

    if (searchEnabled === "true" && quickSearchMobileIcon) {
      this.listeners.push(
        listen(quickSearchMobileIcon, "click", e => {
          e.preventDefault();
          emit("mobile-quick-search:open");
        })
      );
    }

    this.components = [];

    quickSearchContainers.forEach(quickSearchContainer => {
      this.components.push(QuickSearch(quickSearchContainer, this.container));
    });

    this.navigation && this.components.push(this.navigation);

    if (enableStickyHeader) {
      // Our header is always sticky (with position: sticky) however at some
      // point we want to adjust the styling (eg. box-shadow) so we toggle
      // the is-sticky class when our arbitrary space element (.header__space)
      // goes in and out of the viewport.
      this.io = new IntersectionObserver(([{ isIntersecting: visible }]) => {
        toggle(this.container, classes.isSticky, !visible);
        toggle(document.documentElement, classes.stickyActive, !visible);

        // Our navBar (only visible in some layouts) needs to animate: hide / show
        // after we have scrolled down the page enough that the changing of the height
        // will not mess with the above observer.
        if (navBar) {
          toggle(document.documentElement, classes.hideNavBar, !visible);
          toggle(
            document.documentElement,
            classes.navBarToggleVisible,
            !visible
          );

          if (visible) {
            navBarToggle.setAttribute("aria-expanded", false);
          }

          emit("navBar:change");
        }
      });
      this.io.observe(headerSpace);
    }

    // This will watch the height of the header and update the --height-header
    // css variable when necessary. That var gets used for the negative top margin
    // to render the page body under the transparent header
    provideResizeObserver().then(({ ResizeObserver }) => {
      this.ro = new ResizeObserver(([{ target }]) => {
        const headerHeight = target.offsetHeight;

        setHeaderHeightVar(
          target.getBoundingClientRect()
            ? target.getBoundingClientRect().height
            : target.offsetHeight
        );
        setHeaderStickyHeaderHeight(headerHeight);
      });
      this.ro.observe(this.container);

      if (navBar) {
        // This will watch the height of the navigation bar (only visible in some layouts)
        // css variable when necessary. The var is used to ensure it the bar is hidden or shown
        // after enough space has scrolled to ensure the animation does not interfere with the
        // sticky header.
        this.navRo = new ResizeObserver(([{ target }]) => {
          setNavBarHeightVar(target.offsetHeight);
        });
        this.navRo.observe(navBar);
      }
    });

    window.on = on;
    window.emit = emit;
  },

  handleResponse() {
    JSON.parse(this.responseText);
  },

  onBlockSelect({ target }) {
    this.navigation.closeAll();
    add(this.container, classes.dropdownActive);
    add(target, classes.active);
    this.meganavOpenedFromDesignMode = true;
  },

  onBlockDeselect({ target }) {
    remove(this.container, classes.dropdownActive);
    remove(target, classes.active);
    this.meganavOpenedFromDesignMode = false;
  },

  onUnload() {
    this.listeners.forEach(l => l());
    this.components.forEach(c => c.destroy());

    this.io && this.io.disconnect();
    this.navIo && this.navIo.disconnect();
    this.ro.disconnect();
    this.navRo && this.navRo.disconnect();
    this.accountQuickView?.unload();
  },
});
